import { useEffect, useMemo, useState } from "react";
import { ArrowRight, ChevronRight, Plus, Trophy, User, UserCheck, Users } from "lucide-react";
import { useAppSelector } from "../../store/store";
import { useNavigate } from "react-router-dom";
import { Bar, BarChart, CartesianGrid, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import { getPlayerDemographics, getPlayerRegistrationTrends } from "../../api/players";
import { IPlayerDemographic, IPlayerRegistrationTrend } from "../../models/IPlayer";
import { ChartItem, GroupData } from "../../types/chart";
import { DemographicsBar } from "../../components";
import { getPendingActions, getTournamentList } from "../../api/tournaments";
import { ITournamentItem } from "../../models/ITournament";
import { IPendingAction } from "../../models/ITeam";
import { getCoachList } from "../../api/coaches";
import { segmentColor } from "../../components/DemographicsBar";
import { Button, Card, EmptyState, Loader, PageHeader, Select, StatCard, Tag } from "../../ui";
import type { TagTone } from "../../ui";
import moment from "moment";
import "./styles.scss";

// recharts needs literal colours: the brand tokens' values (brand.scss)
const CHART = {
  bar: "#2c42c3", // --brand
  grid: "#e4e4e7", // --line
  axis: "#5c5f66", // --muted
  ink: "#0e1116", // --ink
  cursor: "#f4f4f5", // --alt
};

// A tournament's sport tag: padel blue, volleyball orange, beach volleyball yellow
const sportTone = (type?: string): TagTone => {
  const t = (type || "").toLowerCase();
  if (t.includes("beach volleyball")) return "yellow";
  if (t.includes("padel")) return "brand";
  if (t.includes("volleyball")) return "spark";
  return "neutral";
};

const Home = () => {
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const { currentUser } = useAppSelector((state) => state.auth);
  const [registrationTrendOption, setRegistrationTrendOption] = useState("thisMonth");
  const [playerDemographicOption, setPlayerDemographicOption] = useState("nationality");
  const [playerDemographics, setPlayerDemographics] = useState<IPlayerDemographic>();
  const [tournaments, setTournaments] = useState<ITournamentItem[]>([]);
  const [tournamentsTotal, setTournamentsTotal] = useState(0);
  const [representersTotal, setRepresentersTotal] = useState(0);
  const [pendingActions, setPendingActions] = useState<IPendingAction[]>([]);
  const [playerRegistrationTrends, setPlayerRegistrationTrends] = useState<IPlayerRegistrationTrend>();

  const getPlayerDemographicData = async () => {
    try {
      const res = await getPlayerDemographics();
      setPlayerDemographics(res.data);
    } catch (error: any) {
      console.error(error.message);
    }
  };

  const getRepresentersData = async () => {
    try {
      const res = await getCoachList(1, 10);
      setRepresentersTotal(res.data.pagination.total);
    } catch (error: any) {
      console.error(error.message);
    }
  };

  const getPlayerRegistrationTrendsData = async () => {
    try {
      const res = await getPlayerRegistrationTrends();
      setPlayerRegistrationTrends(res.data);
    } catch (error: any) {
      console.error(error.message);
    }
  };

  const getPendingActionsData = async () => {
    try {
      const res = await getPendingActions();
      setPendingActions(res.data.items);
    } catch (error: any) {
      console.error(error.message);
    }
  };

  const getTournaments = async () => {
    const res = await getTournamentList(1, 3, "", "status", "asc");
    setTournaments(res.data.items.filter((x) => moment().isBefore(x.startDate)));
    setTournamentsTotal(res.data.pagination.total);
  };

  const getData = async () => {
    try {
      await getPlayerDemographicData();
      await getTournaments();
      await getRepresentersData();
      await getPendingActionsData();
      await getPlayerRegistrationTrendsData();
    } catch (error) {
    } finally {
      setLoading(false);
    }
  };

  const getChartData = () => {
    switch (registrationTrendOption) {
      case "thisMonth":
        return { data: playerRegistrationTrends?.thisMonth, xKey: "label" };
      case "monthly":
        return { data: playerRegistrationTrends?.monthly, xKey: "label" };
      case "yearly":
        return { data: playerRegistrationTrends?.yearly, xKey: "label" };
      default:
        return { data: [], xKey: "" };
    }
  };

  useEffect(() => {
    document.title = "Home";
    getData();
  }, []);

  const { data, xKey } = getChartData();

  const playerDemographicGroupData: GroupData = {
    nationality:
      playerDemographics?.nationalityCounts?.map((item) => ({
        name: item.nationality,
        value: item.count,
      })) || [],
    gender:
      playerDemographics?.genderCounts?.map((item) => ({
        name: item.gender,
        value: item.count,
      })) || [],
    tshirtSize:
      playerDemographics?.tshirtSizeCounts?.map((item) => ({
        name: item.tshirtSize,
        value: item.count,
      })) || [],
  };

  const playerDemographicChartData: ChartItem[] = useMemo(() => {
    const selectedGroup = playerDemographicGroupData[playerDemographicOption as keyof GroupData];
    const sortedGroup = [...selectedGroup].sort((a, b) => b.value - a.value);

    const top3 = sortedGroup.slice(0, 3);
    const others = sortedGroup.slice(3);
    const othersTotal = others.reduce((sum, item) => sum + item.value, 0);

    const totalPlayers = playerDemographics?.totalPlayers || 1;

    const rawData = [
      ...top3.map((item) => ({
        name: item.name,
        value: item.value,
        rawPercent: (item.value / totalPlayers) * 100,
      })),
      ...(othersTotal > 0
        ? [
            {
              name: "Others",
              value: othersTotal,
              rawPercent: (othersTotal / totalPlayers) * 100,
            },
          ]
        : []),
    ];

    let remaining = 100;
    return rawData.map((item, index) => {
      const isLast = index === rawData.length - 1;
      const rounded = isLast ? remaining : Math.floor(item.rawPercent);
      remaining -= rounded;

      return {
        name: item.name,
        value: item.value,
        percent: rounded,
        color: segmentColor(index, item.name),
      };
    });
  }, [playerDemographicGroupData, playerDemographics, playerDemographicOption]);

  return (
    <div className="home-page">
      {loading ? (
        <Loader />
      ) : (
        <>
          <PageHeader
            title={
              <>
                Welcome back, <span className="home-first-name">{currentUser?.firstName}</span>
              </>
            }
            subtitle={
              <>
                You have {pendingActions.length > 0 ? pendingActions.length : "no"} pending{" "}
                {pendingActions.length === 1 ? "action" : "actions"}
              </>
            }
            actions={
              <Button
                variant="primary"
                icon={<User size={16} />}
                onClick={() => window.open(process.env.REACT_APP_REFEREE_PORTAL_URL, "_blank")}
                data-tooltip="Referee Portal"
              >
                Referee Portal
              </Button>
            }
          />
          <div className="overview-cards">
            <StatCard
              label="Tournaments"
              value={tournamentsTotal}
              icon={<Trophy />}
              tone="spark"
              onClick={() => navigate("tournaments")}
              hint={
                <span className="stat-link">
                  View tournaments <ArrowRight size={14} />
                </span>
              }
            />
            <StatCard
              label="Players"
              value={playerDemographics?.totalPlayers}
              icon={<Users />}
              tone="brand"
              onClick={() => navigate("players")}
              hint={
                <span className="stat-link">
                  View players <ArrowRight size={14} />
                </span>
              }
            />
            <StatCard
              label="Representers"
              value={representersTotal}
              icon={<UserCheck />}
              tone="spark"
              onClick={() => navigate("representers")}
              hint={
                <span className="stat-link">
                  View representers <ArrowRight size={14} />
                </span>
              }
            />
          </div>
          <div className="content">
            <div className="left">
              <Card
                className="registration-trend-card"
                title="Player Registration Trends"
                actions={
                  <Select
                    size="sm"
                    aria-label="Player Registration Trends"
                    value={registrationTrendOption}
                    onChange={(evt) => setRegistrationTrendOption(evt.target.value)}
                  >
                    <option value="thisMonth">This Month</option>
                    <option value="monthly">Monthly</option>
                    <option value="yearly">Yearly</option>
                  </Select>
                }
              >
                <ResponsiveContainer width="100%" height={240}>
                  <BarChart data={data} margin={{ top: 8, right: 4, left: -24, bottom: 0 }}>
                    <CartesianGrid vertical={false} stroke={CHART.grid} />
                    <XAxis
                      dataKey={xKey}
                      tickLine={false}
                      axisLine={{ stroke: CHART.grid }}
                      tick={{ fill: CHART.axis, fontSize: 12 }}
                    />
                    <YAxis axisLine={false} tickLine={false} allowDecimals={false} tick={{ fill: CHART.axis, fontSize: 11 }} />
                    <Tooltip
                      cursor={{ fill: CHART.cursor }}
                      contentStyle={{
                        border: `1px solid ${CHART.grid}`,
                        borderRadius: 4,
                        boxShadow: "0 10px 30px rgba(14, 17, 22, 0.14)",
                        fontSize: 13,
                      }}
                      labelStyle={{ color: CHART.ink, fontWeight: 600 }}
                      itemStyle={{ color: CHART.bar }}
                    />
                    <Bar dataKey="value" fill={CHART.bar} radius={[3, 3, 0, 0]} barSize={16} />
                  </BarChart>
                </ResponsiveContainer>
              </Card>
              <Card className="player-demographic-card" title="Player Demographic">
                <div className="group-by-container">
                  <p>Group by</p>
                  <Select
                    size="sm"
                    aria-label="Group by"
                    value={playerDemographicOption}
                    onChange={(evt) => setPlayerDemographicOption(evt.target.value)}
                  >
                    <option value="nationality">Nationality</option>
                    <option value="tshirtSize">Tshirt Size</option>
                    <option value="gender">Gender</option>
                  </Select>
                </div>
                <DemographicsBar data={playerDemographicChartData} />
              </Card>
            </div>
            <div className="right">
              <Card
                className="pending-action-card"
                padding="sm"
                title={
                  <span className="title-with-count">
                    Pending Actions
                    {pendingActions.length > 0 && <Tag tone="solid-spark">{pendingActions.length}</Tag>}
                  </span>
                }
              >
                {pendingActions.length > 0 ? (
                  pendingActions.map((action, id) =>
                    id < 3 ? (
                      <div
                        key={id}
                        className="pending-action-item"
                        onClick={() =>
                          navigate(
                            `tournaments/${action.tournamentId}/${action.tournamentCategoryId}/teams/${action.teamId}?memberId=${action.teamMemberId}`
                          )
                        }
                      >
                        <div className="pending-action-text">
                          <div className="name-container">
                            <p className="name">
                              {action.playerName} <span>{action.nationality.slice(0, 3)}</span>
                            </p>
                            <Tag tone="warn">{action.registrationStatus}</Tag>
                          </div>
                          <p className="tournament-name">{action.tournamentName}</p>
                          <div className="info">
                            <p>{action.categoryName}</p>
                            <span className="separator" />
                            <p>{action.teamName}</p>
                          </div>
                        </div>
                        <ChevronRight size={18} className="chevron" />
                      </div>
                    ) : null
                  )
                ) : (
                  <EmptyState compact title="There are no pending actions" />
                )}
              </Card>

              <Card className="upcoming-tournaments" padding="sm" title="Upcoming Tournaments">
                {tournaments.map((item, id) => (
                  <div key={id} className="tournament-item" onClick={() => navigate(`tournaments/${item.id}`)}>
                    <div className="date-container">
                      <p className="day">{moment(item.startDate).format("DD")}</p>
                      <p className="month">{moment(item.startDate).format("MMM YYYY")}</p>
                    </div>
                    <div className="detail-container">
                      <p className="name" data-tooltip={item.name}>
                        {item.name}
                      </p>
                      <div className="details-row">
                        <p data-tooltip={item.location?.name || "-"}>{item.location?.name || "-"}</p>
                        <Tag tone={sportTone(item.type)}>
                          {item.type}
                        </Tag>
                      </div>
                    </div>
                    <ChevronRight size={18} className="chevron" />
                  </div>
                ))}
                <Button
                  variant="ghost"
                  block
                  className="create-tournament"
                  icon={<Plus size={16} />}
                  onClick={() => (window.location.href = "/dashboard/add-tournament?goTo=home")}
                >
                  Create new tournament
                </Button>
              </Card>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default Home;
